import { alert } from "@components/common/Alert";
import { MarkdownContent } from "@components/design-system/atoms/MarkdownContent";
import { DSCircularButton } from "@components/design-system/molecules";
import { DSCopyButton } from "@components/design-system/organisms";
import {
  faChevronDown,
  faPaperclip,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  AvailableCompanions,
  CompanionIntroMessage,
  useCompanion,
} from "@hooks/use-companion";
import { useStreamedMessage } from "@hooks/use-streamed-message";
import Twemoji from "@src/Twemoji";
import { stripNbsp } from "@src/utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import moment from "moment";
import { useEffect, useRef, useState } from "react";

import { LegacyBadge } from "./Buttons";
import { CompanionChatInputSection } from "./CompanionChatInput";
import { CompanionSessionTask } from "./CompanionTasks";

export const CompanionSessionChat = () => {
  const { companionSession } = useCompanion();

  if (!companionSession?.companionSessionUuid) {
    return null;
  }

  const filename = companionSession.attachments?.[0]?.fileName;

  const status = "ready"; // hardcoded since these are all legacy now

  return (
    <div className="flex flex-1 flex-col h-full">
      <StatusPill
        status={status}
        statusText={companionSession.statusText}
      />
      <div className="rounded-xl bg-primary flex flex-1 flex-col justify-between p-2 text-primary overflow-hidden">
        <div className="flex flex-col px-2 pb-1 min-w-0">
          <div className="flex items-center gap-2">
            <b className="truncate">
              {companionSession.title || "New Companion Chat"}
            </b>
            <LegacyBadge />
          </div>
          {moment(companionSession.createdAt).format(
            "MMM Do, h:mm A",
          )}
        </div>
        <CompanionChatMessages status={status} />
        <div>
          <FileAttachmentIcon filename={filename} />
          <CompanionChatInputSection />
        </div>
      </div>
    </div>
  );
};

const CompanionTutorialMessages = ({ stream = false }) => {
  const { releaseGlobalCompanionLlmRouter } = useFlags();

  if (releaseGlobalCompanionLlmRouter) {
    return <CompanionUnifiedIntroMessage stream={stream} />;
  }

  return <CompanionLegacyTutorialMessages stream={stream} />;
};

const CompanionUnifiedIntroMessage = ({ stream = false }) => {
  const textContent = useStreamedMessage({
    message: CompanionIntroMessage,
    stream,
  });

  return (
    <TextBubble
      key="tutorialMessage"
      message={{
        textContent,
        role: "assistant",
      }}
      hideCopy={true}
    />
  );
};

const CompanionLegacyTutorialMessages = ({ stream = false }) => {
  const [messageIdx, setMessageIdx] = useState(0);

  const messages = [
    {
      textContent: useStreamedMessage({
        message: AvailableCompanions.SUMMI?.welcomeMessage,
        stream,
        onCompleted: () => {
          setTimeout(() => {
            setMessageIdx(1);
          }, 500);
        },
      }),
      companionType: "SUMMI",
    },
    {
      textContent: useStreamedMessage({
        message: AvailableCompanions.QUILL?.welcomeMessage,
        stream,
        onCompleted: () => {
          setTimeout(() => {
            setMessageIdx(2);
          }, 500);
        },
        skip: stream && messageIdx !== 1,
      }),

      companionType: "QUILL",
    },
    {
      textContent: useStreamedMessage({
        message: AvailableCompanions.SAGE?.welcomeMessage,
        stream,
        onCompleted: () => {
          setTimeout(() => {
            setMessageIdx((prev) => prev + 1);
          }, 500);
        },
        skip: stream && messageIdx !== 2,
      }),
      companionType: "SAGE",
    },
    {
      textContent: useStreamedMessage({
        message:
          "We're still learning, and AI can make mistakes. Remember to double-check our work!",
        stream,
        skip: stream && messageIdx !== 3,
      }),
    },
  ];

  return (
    <>
      {messages.map((message, idx) => (
        <TextBubble
          key={`tutorialMessage${idx}`}
          message={{
            textContent: message.textContent,
            role: "assistant",
            companionType: message.companionType,
          }}
          hideCopy={true}
        />
      ))}
    </>
  );
};

const CompanionChatMessages = ({ status }) => {
  const messageContainerRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const { companionSession } = useCompanion();

  const messages = companionSession?.messages;
  const tasks = companionSession?.tasks;

  const handleScroll = () => {
    const container = messageContainerRef.current;
    if (!container) return;
    const threshold = 10;
    const nearBottom =
      container.scrollHeight -
        container.scrollTop -
        container.clientHeight <
      threshold;
    isNearBottomRef.current = nearBottom;
    setShowScrollToBottom(!nearBottom);
  };

  const scrollToBottom = () => {
    const container = messageContainerRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  };

  // Re-evaluate near-bottom state when the container resizes (e.g. sidebar drag)
  useEffect(() => {
    const container = messageContainerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(() => handleScroll());
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const container = messageContainerRef.current;
    if (!container) return;

    if (isNearBottomRef.current) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages.length, tasks.length]);

  const allChatItems = [
    ...messages.map((i) => {
      return {
        ...i,
        objectType: "message",
      };
    }),
    ...tasks.map((i) => {
      return {
        ...i,
        objectType: "task",
      };
    }),
  ].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  return (
    <div className="relative flex flex-col flex-1 min-h-0">
      <div
        className="flex flex-col flex-1 gap-2 overflow-auto py-2 overscroll-contain"
        ref={messageContainerRef}
        onScroll={handleScroll}
      >
        <CompanionTutorialMessages stream={!allChatItems?.length} />
        {allChatItems?.map((item) => {
          if (item.objectType === "message") {
            return (
              <TextBubble
                key={item.companionSessionMessageUuid}
                message={item}
                hideCopy={item.hideCopy || false}
              />
            );
          } else if (item.objectType === "task") {
            return (
              <CompanionSessionTask
                key={item.companionSessionTaskUuid}
                task={item}
                defaultExpanded={true}
              />
            );
          }
        })}
        <InProgressMessage status={status} />
      </div>
      {showScrollToBottom && (
        <div className="absolute w-full bottom-2 flex justify-center z-10 pointer-events-none">
          <div className="pointer-events-auto">
            <DSCircularButton
              size="sm"
              icon={faChevronDown}
              onClick={scrollToBottom}
              dataTestId="companion-scroll-to-bottom"
              aria-label="Scroll to bottom"
            />
          </div>
        </div>
      )}
    </div>
  );
};

const InProgressMessage = ({ status }) => {
  const { mostRecentCompanionUsed } = useCompanion();
  const isSage = mostRecentCompanionUsed === "SAGE";
  if (["ready", "waiting"].includes(status)) {
    return null;
  }
  return (
    <>
      <TextBubble
        key="inProgressMessage"
        message={{
          textContent: (
            <div className="p-2">
              <StatusDots numDots={3} iconColor={"bg-gray-400"} />
            </div>
          ),
          role: "assistant",
        }}
        hideCopy={true}
      />
      {isSage && (
        <p className="text-xs text-secondary mt-1 px-1">
          Please note that Sage takes longer than other companions to
          respond. Please wait while Sage researches the answer!
        </p>
      )}
    </>
  );
};

function StatusPill({ status, statusText = null }) {
  const statusMap = {
    ready: {
      displayText: "Ready for input",
      statusColor: "bg-green-400 text-white",
      iconColor: "bg-green-200",
      animated: false,
    },
    processing: {
      displayText: statusText || "Processing",
      statusColor: "bg-yellow-400 text-white",
      iconColor: "bg-yellow-200",
      animated: true,
    },
    thinking: {
      displayText: statusText || "Thinking",
      statusColor: "bg-pink-400 text-white",
      iconColor: "bg-pink-200",
      animated: true,
    },
    waiting: {
      displayText: "Waiting for Upload",
      statusColor: "bg-indigo-400 text-white",
      iconColor: "bg-indigo-200",
      animated: false,
    },
  };
  const { displayText, statusColor, iconColor, animated } =
    statusMap[status];

  return (
    <div
      className={`flex items-center justify-between gap-3 rounded-full px-4 py-2 mb-2 text-xs ${statusColor}`}
      data-testid="companion-status-pill"
    >
      <span className="font-bold text-sm">{displayText}</span>
      <StatusDots
        iconColor={iconColor}
        numDots={3}
        animated={animated}
      />
    </div>
  );
}

const StatusDots = ({ iconColor, numDots = 4, animated = true }) => {
  return (
    <div className="flex space-x-1 items-center">
      {[...Array(numDots).keys()].map((i) => (
        <div
          key={`status-icon-${i}`}
          className={`flex items-center justify-center ${
            animated && "animate-[bouncePulse_1s_infinite]"
          }`}
          // Stagger animation with 0.1s delay between dots for wave-like visual effect
          style={{ animationDelay: `${i * 0.1}s` }}
        >
          <div
            className={`w-1.5 h-1.5 ${iconColor} rounded-full`}
          ></div>
        </div>
      ))}
    </div>
  );
};

function TextBubble({ message, hideCopy = false, hideEmpty = true }) {
  const { role, textContent, createdAt, companionType } = message;
  const [shouldShowDate, setShouldShowDate] = useState(false);
  const isUser = role === "user";
  const companion = !isUser
    ? (AvailableCompanions[companionType] ?? AvailableCompanions.OTTO)
    : null;
  const companionIcon = companion?.headImage ? (
    <img
      src={companion.headImage}
      alt={companion.displayName}
      className="w-7 h-7 rounded-full object-cover"
    />
  ) : companion?.emoji ? (
    <Twemoji emoji={companion.emoji} />
  ) : null;

  const onCopy = async () => {
    await navigator.clipboard.writeText(stripNbsp(textContent));
    alert("info", "Copied!");
  };

  if (!message?.textContent && hideEmpty) {
    return null;
  }

  return (
    <div
      className={`w-full flex flex-col px-sm my-xs ${
        isUser ? "items-end" : "items-start"
      }`}
      data-testid={`companion-message${
        message.companionSessionMessageUuid
          ? `-${message.companionSessionMessageUuid}`
          : ""
      }`}
      onClick={() => setShouldShowDate(!shouldShowDate)}
    >
      <div
        className={`relative max-w-[80%] ${
          isUser
            ? "bg-brand text-white"
            : `bg-secondary text-primary ${companionIcon ? "ml-6" : ""}`
        } rounded-large px-md py-sm`}
      >
        {companionIcon && (
          <div className={"absolute -left-8 -bottom-1 text-lg"}>
            {companionIcon}
          </div>
        )}

        <div
          className={`absolute ${
            isUser
              ? "-right-1 bottom-0 bg-indigo-400"
              : "-left-1 bottom-0 bg-secondary"
          }  w-2 h-2 rounded-full`}
        />

        <div
          className={`whitespace-pre-wrap wrap-break-word text-sm ${
            isUser && "selection-invert"
          }`}
        >
          {/* This is to handle passing react components like StatusDots */}
          {typeof textContent !== "string" ? (
            textContent
          ) : (
            <MarkdownContent
              content={textContent}
              className="text-sm"
              isCompanion
            />
          )}
        </div>
        {!isUser && !hideCopy && (
          <div className="absolute -right-10 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white text-indigo-600 shadow-sm flex items-center justify-center dark:bg-gray-700/50 dark:text-gray-300 dark:hover:bg-gray-700">
            <DSCopyButton text={textContent} onClick={onCopy} />
          </div>
        )}
      </div>

      {createdAt && shouldShowDate && (
        <div className="text-xs text-secondary">
          {moment(createdAt).format("MMM Do, h:mm A")}
        </div>
      )}
    </div>
  );
}

export const FileAttachmentIcon = ({ filename }) => {
  if (!filename) {
    return null;
  }

  return (
    <div
      className="inline-flex bg-tertiary rounded-lg items-center gap-2 p-1.5 mb-2 text-primary"
      data-testid="companion-attachment"
    >
      <FontAwesomeIcon icon={faPaperclip} />
      <span>{filename}</span>
    </div>
  );
};
